import { parseAdkEvent } from '../adkEventParser';
import { ADK_EVENT_TYPES } from '../constants';

/**
 * Merges arrays while preventing duplicates based on a unique key.
 * Needed because streaming events may send the same components/vulnerabilities
 * multiple times as data arrives in chunks. This ensures each item appears only once.
 */
function mergeUniqueByKey(existing, newItems, getKey) {
  if (!existing || existing.length === 0) {
    return newItems || [];
  }
  if (!newItems || newItems.length === 0) {
    return existing;
  }
  
  const existingKeys = new Set(existing.map(getKey));
  const uniqueNewItems = newItems.filter(item => !existingKeys.has(getKey(item)));
  return [...existing, ...uniqueNewItems];
}

function getComponentKey(component) {
  return `${component.name}-${component.version}-${component.vendor || ''}`;
}

function getVulnerabilityKey(vulnerability) {
  return vulnerability.vulnerability_name || 
         vulnerability['Vulnerability Name'] || 
         vulnerability.vulnerabilityName || 
         vulnerability.name || 
         vulnerability.cve || 
         '';
}

function handleStructuredData(message, event) {
  const updated = { ...message };
  
  if (event.components && Array.isArray(event.components)) {
    updated.components = mergeUniqueByKey(
      message.components,
      event.components,
      getComponentKey
    );
  }
  
  if (event.vulnerabilities && Array.isArray(event.vulnerabilities)) {
    updated.vulnerabilities = mergeUniqueByKey(
      message.vulnerabilities,
      event.vulnerabilities,
      getVulnerabilityKey
    );
  }
  
  return updated;
}

/**
 * Format error message to be user-friendly
 * @param {string} error - Raw error message
 * @param {string} errorCode - Error code (e.g., "429", "RESOURCE_EXHAUSTED")
 * @param {string} status - Status code
 * @returns {string} Formatted error message
 */
// TODO: This is a temporary function to format error messages and display them in the chat for testing purposes.
// We should get consensus if we want to keep this function or not.
function formatErrorMessage(error, errorCode, status) {
  // Guard against undefined/null/empty error - required because we call error.includes() later
  if (!error || typeof error !== 'string') {
    return 'Sorry, I encountered an error. Please try again.';
  }

  // Handle 429 / Resource Exhausted errors
  if (
    errorCode === '429' ||
    errorCode === 'RESOURCE_EXHAUSTED' ||
    status === 'RESOURCE_EXHAUSTED' ||
    error.includes('429') ||
    error.includes('Resource exhausted') ||
    error.includes('RESOURCE_EXHAUSTED')
  ) {
    return 'Rate limit exceeded. The service is currently experiencing high demand. Please wait a moment and try again.';
  }

  // Handle other common errors
  if (errorCode === '500' || error.includes('500')) {
    return 'Server error. Please try again in a moment.';
  }

  if (errorCode === '503' || error.includes('503')) {
    return 'Service temporarily unavailable. Please try again later.';
  }

  // Extract user-friendly message from error text
  // Remove technical details like "Error while running agent: failed to call model:"
  let message = error;
  if (message.includes('Error while running agent:')) {
    message = message.replace(/Error while running agent:\s*/, '');
  }
  if (message.includes('failed to call model:')) {
    message = message.replace(/failed to call model:\s*/, '');
  }

  // If message is still too technical, provide a generic one
  if (message.length > 200 || message.includes('Status:') || message.includes('Details:')) {
    return 'An error occurred while processing your request. Please try again.';
  }

  return `An error occurred while processing your request: ${message}`;
}

/**
 * Custom hook to handle SSE streaming and event processing
 * Returns event handlers for ADK events
 */
export function useChatStream({
  setMessages,
  allEventsRef,
  processedChunkIds,
  lastProcessedInvocationId
}) {
  const handleAdkEvent = event => {
    switch (event.type) {
      case ADK_EVENT_TYPES.TEXT_MESSAGE_START:
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: '',
            timestamp: new Date()
          }
        ]);
        if (event.rawEvent?.invocationId) {
          lastProcessedInvocationId.current = event.rawEvent.invocationId;
        }
        break;

      case ADK_EVENT_TYPES.TEXT_MESSAGE_CONTENT:
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          
          if (!lastMessage || lastMessage.role !== 'assistant') {
            return newMessages;
          }
          
          const hasStructuredData = !!(event.components || event.vulnerabilities);
          
          // Handle structured data updates (components/vulnerabilities)
          if (hasStructuredData) {
            const updated = handleStructuredData(lastMessage, event);
            Object.assign(lastMessage, updated);
          }
          
          // Process text content - allow it even if message has components/vulnerabilities
          // This allows presenter to show both text and tables
          if (event.content) {
            const newContent = event.content || '';
            const currentContent = lastMessage.content || '';
            lastMessage.content = event.isFullContent ? newContent : currentContent + newContent;
          }
          
          return newMessages;
        });
        break;

      case ADK_EVENT_TYPES.TEXT_MESSAGE_END:
        break;

      case ADK_EVENT_TYPES.TOOL_CALL_START:
      case ADK_EVENT_TYPES.TOOL_CALL_RESULT:
      case ADK_EVENT_TYPES.TOOL_CALL_END:
        break;

      case ADK_EVENT_TYPES.ERROR:
        // Handle error events from the stream
        console.log('useChatStream: Received ERROR event:', event);
        const errorMessage = formatErrorMessage(event.error, event.errorCode, event.status);
        console.log('useChatStream: Formatted error message:', errorMessage);
        
        setMessages(prev => {
          if (prev.length === 0) {
            return [{
              role: 'assistant',
              content: errorMessage,
              error: true,
              timestamp: new Date(event.timestamp)
            }];
          }
          
          const lastMessage = prev[prev.length - 1];
          if (lastMessage.role === 'assistant') {
            // Update the last assistant message with error
            return [
              ...prev.slice(0, -1),
              {
                ...lastMessage,
                content: errorMessage,
                error: true,
                timestamp: new Date(event.timestamp)
              }
            ];
          }
          
          // Create new error message if no assistant message exists
          return [
            ...prev,
            {
              role: 'assistant',
              content: errorMessage,
              error: true,
              timestamp: new Date(event.timestamp)
            }
          ];
        });
        break;

      case ADK_EVENT_TYPES.RUN_FINISHED:
        break;

      case ADK_EVENT_TYPES.UNKNOWN:
        if (process.env.NODE_ENV === 'development') {
          console.debug('Unknown ADK event format:', event.rawEvent);
        }
        break;
    }
  };

  const processSSEEvent = rawData => {
    // Track all events for debugging
    allEventsRef.current.push({
      timestamp: Date.now(),
      data: rawData,
      keys: Object.keys(rawData)
    });

    // Parse as ADK event
    const adkEvents = parseAdkEvent(rawData, processedChunkIds.current, {
      lastProcessedInvocationId
    });

    // Handle normalized ADK events
    for (const event of adkEvents) {
      handleAdkEvent(event);
    }
  };

  return { processSSEEvent };
}
